import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { useBlendy } from "@/contexts/BlendyContext"
import { isMobileOrReducedMotion } from "@/utils/device"
import { cn } from "@/lib/utils"

const DialogContext = React.createContext({
    blendyId: "",
})

const Dialog = ({ onOpenChange, ...props }) => {
    const rawId = React.useId()
    const blendyId = React.useMemo(() => `dialog-${rawId.replace(/:/g, "")}`, [rawId])
    const { blendy, enabled: blendyEnabled, update: updateBlendy } = useBlendy()

    const canAnimate = blendyEnabled && !isMobileOrReducedMotion() && Boolean(blendy)

    const handleOpenChange = (nextOpen) => {
        if (!onOpenChange) return

        if (!canAnimate) {
            onOpenChange(nextOpen)
            return
        }

        if (nextOpen) {
            onOpenChange(true)
            requestAnimationFrame(() => {
                updateBlendy()
                blendy.toggle(blendyId)
            })
            return
        }

        // Fallback close guard: some modals do not originate from a DialogTrigger,
        // which can cause blendy untoggle callbacks to never fire.
        let didClose = false
        const finishClose = () => {
            if (didClose) return
            didClose = true
            onOpenChange(false)
        }

        try {
            blendy.untoggle(blendyId, finishClose)
            setTimeout(finishClose, 280)
        } catch {
            finishClose()
        }
    }

    return (
        <DialogContext.Provider value={{ blendyId }}>
            <DialogPrimitive.Root {...props} onOpenChange={handleOpenChange} />
        </DialogContext.Provider>
    )
}

const DialogTrigger = React.forwardRef(({ children, ...props }, ref) => {
    const { blendyId } = React.useContext(DialogContext)

    return (
        <DialogPrimitive.Trigger ref={ref} data-blendy-from={blendyId} {...props}>
            {children}
        </DialogPrimitive.Trigger>
    )
})
DialogTrigger.displayName = DialogPrimitive.Trigger.displayName

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            "fixed inset-0 z-[100] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            className
        )}
        {...props}
    />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
    const { blendyId } = React.useContext(DialogContext)

    return (
        <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Content
                ref={ref}
                data-blendy-to={blendyId}
                className={cn(
                    "fixed left-[50%] top-[50%] z-[100] grid w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg max-h-[90vh] overflow-y-auto hide-scrollbar",
                    className
                )}
                {...props}
            >
                <div>
                    {children}
                    <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                        <X className="h-4 w-4" />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                </div>
            </DialogPrimitive.Content>
        </DialogPortal>
    )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
    className,
    ...props
}) => (
    <div
        className={cn(
            "flex flex-col space-y-1.5 text-center sm:text-left",
            className
        )}
        {...props}
    />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
    className,
    ...props
}) => (
    <div
        className={cn(
            "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
            className
        )}
        {...props}
    />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn(
            "text-lg font-semibold leading-none tracking-tight",
            className
        )}
        {...props}
    />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
    />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
}
